import { ChevronDown, Search } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { OFFER_MERGE_FIELDS, type OfferMergeField } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Input } from '../../../../components/Input';
import { Popover, usePopover } from '../../../../components/Popover';
import { useFocusOnOpen } from '../../../../lib/use-focus-on-open';
import { matchingFields } from './offer-letter-form';
import styles from './InsertFieldMenu.module.css';

const MENU_WIDTH = 280;

/**
 * "Insert a field" (Option A): one button in place of the 18 field buttons, so the letter stays above the fold. It
 * opens beside itself, with the search already focused: type to filter, the arrow keys to move, Enter to put the field
 * where the cursor was in the letter, Esc to close (06-qol §2).
 */
export function InsertFieldMenu({ onInsert, disabled = false }: { readonly onInsert: (key: OfferMergeField) => void; readonly disabled?: boolean }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const search = useRef<HTMLInputElement | null>(null);
  const listId = useId();
  const fields = matchingFields(query);
  useFocusOnOpen(search, isOpen);

  const open = (): void => {
    setQuery('');
    setActive(0);
    toggle();
  };

  const choose = (key: OfferMergeField): void => {
    close();
    onInsert(key);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (fields.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + fields.length) % fields.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const field = fields[Math.min(active, fields.length - 1)];
      if (field) choose(field.key);
    }
  };

  const activeField = fields[Math.min(active, fields.length - 1)];
  const activeId = activeField ? `${listId}-${activeField.key}` : undefined;
  // The arrow keys keep the chosen field in view inside the short list.
  useEffect(() => {
    if (activeId) document.getElementById(activeId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId]);
  return (
    <>
      <Button ref={triggerRef} size="sm" variant="outline" disabled={disabled} aria-expanded={isOpen} aria-haspopup="dialog" onClick={open}>
        Insert a field
        <ChevronDown size={14} aria-hidden />
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={MENU_WIDTH} label="Insert a field">
        <div className={styles.menu}>
          <Input
            ref={search}
            icon={Search}
            value={query}
            placeholder="Find a field"
            aria-label="Find a field"
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={activeId}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          <ul id={listId} className={styles.list} role="listbox" aria-label="Letter fields">
            {fields.map((field, index) => (
              <li
                key={field.key}
                id={`${listId}-${field.key}`}
                role="option"
                aria-selected={index === active}
                className={styles.option}
                onPointerEnter={() => setActive(index)}
                // A click, not a pointer-down: on a phone a finger lands on an option to scroll the list.
                onClick={() => choose(field.key)}
              >
                {field.label}
              </li>
            ))}
          </ul>
          {fields.length === 0 && <p className={styles.none}>No field is called that. Try “salary” or “date”.</p>}
          <p className={styles.foot}>
            {OFFER_MERGE_FIELDS.length} fields · type to filter · Enter puts it at the cursor
          </p>
        </div>
      </Popover>
    </>
  );
}
